import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiAward, FiUsers, FiFilter } from 'react-icons/fi';
import { getApiUrl } from '../utils/api';
import ReputationBadge from '../components/competition/ReputationBadge';
import RankSparkline, { RankTrendIndicator } from '../components/leaderboard/RankSparkline';

interface UserRanking {
  user_id: number;
  email: string;
  total_points: number;
  tier: string;
  competition_wins: number;
  competition_top_3: number;
  total_entries: number;
  best_deflated_sharpe: number | null;
  avg_rank_percentile: number | null;
  /** Historical rank positions (latest 30 days) */
  rank_history?: number[];
  /** Previous rank for trend indicator */
  previous_rank?: number;
}

export default function LeaderboardPage() {
  const [rankings, setRankings] = useState<UserRanking[]>([]);
  const [myReputation, setMyReputation] = useState<UserRanking | null>(null);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState<string>('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchRankings();
    fetchMyReputation();
  }, []);

  const fetchRankings = async () => {
    try {
      const token = localStorage.getItem('authToken');
      // Note: This endpoint would need to be added to the backend
      // For now, we'll simulate with available data
      const response = await fetch(`${getApiUrl()}/api/v1/competitions?active_only=false`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // In a real implementation, this would fetch aggregated user rankings
        // For now, we'll show a placeholder
        setRankings([]);
      }
    } catch (err) {
      console.error('Error fetching rankings:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyReputation = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/v1/competitions/me/reputation`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMyReputation(data);
      }
    } catch (err) {
      console.error('Error fetching reputation:', err);
    }
  };

  const tierOrder = ['diamond', 'gold', 'silver', 'bronze'];

  const filteredRankings = rankings.filter(r =>
    tierFilter === 'all' || r.tier === tierFilter
  ).sort((a, b) => b.total_points - a.total_points);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <FiAward className="w-8 h-8 text-yellow-500" />
            Global Leaderboard
          </h1>
          <p className="mt-2 text-gray-400">
            Top traders ranked by competition performance
          </p>
        </div>

        {/* My Stats Card */}
        {myReputation && (
          <div className="mb-8 p-6 bg-gray-900 border border-gray-800 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <ReputationBadge tier={myReputation.tier} size="xl" />
                <div>
                  <h2 className="text-xl font-semibold text-white">Your Ranking</h2>
                  <p className="text-gray-400">
                    {myReputation.total_points} points • {myReputation.total_entries} competitions entered
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-8 text-center">
                <div>
                  <p className="text-3xl font-bold text-yellow-400 tabular-nums">{myReputation.competition_wins}</p>
                  <p className="text-sm text-gray-500">Wins</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-white tabular-nums">{myReputation.competition_top_3}</p>
                  <p className="text-sm text-gray-500">Top 3</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-white tabular-nums">
                    {myReputation.best_deflated_sharpe?.toFixed(2) ?? '-'}
                  </p>
                  <p className="text-sm text-gray-500">Best DSR</p>
                </div>
              </div>
            </div>

            {/* Progress to next tier */}
            {myReputation.tier !== 'diamond' && (
              <div className="mt-4 pt-4 border-t border-gray-800">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-400">Progress to {
                    myReputation.tier === 'bronze' ? 'Silver' :
                    myReputation.tier === 'silver' ? 'Gold' : 'Diamond'
                  }</span>
                  <span className="text-gray-400">{myReputation.total_points} / {
                    myReputation.tier === 'bronze' ? 100 :
                    myReputation.tier === 'silver' ? 500 : 2000
                  } points</span>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-profit transition-all"
                    style={{
                      width: `${Math.min(100, (myReputation.total_points / (
                        myReputation.tier === 'bronze' ? 100 :
                        myReputation.tier === 'silver' ? 500 : 2000
                      )) * 100)}%`
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tier Filter */}
        <div className="flex items-center gap-2 mb-6">
          <FiFilter className="w-4 h-4 text-gray-400" />
          {['all', ...tierOrder].map((tier) => (
            <button
              key={tier}
              onClick={() => setTierFilter(tier)}
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                tierFilter === tier
                  ? tier === 'diamond' ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50' :
                    tier === 'gold' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50' :
                    tier === 'silver' ? 'bg-gray-300/20 text-gray-300 border border-gray-300/50' :
                    tier === 'bronze' ? 'bg-amber-600/20 text-amber-500 border border-amber-600/50' :
                    'bg-white text-black'
                  : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              {tier.charAt(0).toUpperCase() + tier.slice(1)}
            </button>
          ))}
        </div>

        {/* Rankings Table */}
        {filteredRankings.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-12 text-center">
            <FiUsers className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Leaderboard Coming Soon</h3>
            <p className="text-gray-400 max-w-md mx-auto">
              Enter competitions to climb the global rankings and earn reputation points.
            </p>
            <button
              onClick={() => navigate('/dashboard/competitions')}
              className="mt-6 px-6 py-2 bg-white text-black font-medium rounded-md hover:bg-gray-100 transition-colors"
            >
              Browse Competitions
            </button>
          </div>
        ) : (
          <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Rank</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Trader</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Tier</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Points</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Wins</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Top 3</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Best DSR</th>
                  <th className="text-center py-3 px-4 text-gray-400 font-medium">Trend</th>
                </tr>
              </thead>
              <tbody>
                {filteredRankings.map((ranking, i) => (
                  <tr key={ranking.user_id} className="border-t border-gray-800 hover:bg-gray-800/30">
                    <td className="py-4 px-4">
                      <span className={`font-bold tabular-nums ${
                        i === 0 ? 'text-yellow-400' :
                        i === 1 ? 'text-gray-300' :
                        i === 2 ? 'text-amber-600' :
                        'text-gray-500'
                      }`}>
                        #{i + 1}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-white">{ranking.email.split('@')[0]}</td>
                    <td className="py-4 px-4">
                      <ReputationBadge tier={ranking.tier} size="sm" />
                    </td>
                    <td className="py-4 px-4 text-right tabular-nums text-white font-medium">
                      {ranking.total_points.toLocaleString()}
                    </td>
                    <td className="py-4 px-4 text-right tabular-nums text-yellow-400">
                      {ranking.competition_wins}
                    </td>
                    <td className="py-4 px-4 text-right tabular-nums text-gray-300">
                      {ranking.competition_top_3}
                    </td>
                    <td className="py-4 px-4 text-right tabular-nums">
                      <span className={ranking.best_deflated_sharpe && ranking.best_deflated_sharpe > 1 ? 'text-profit' : 'text-gray-400'}>
                        {ranking.best_deflated_sharpe?.toFixed(2) ?? '-'}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-center gap-2">
                        {ranking.rank_history && ranking.rank_history.length > 1 ? (
                          <RankSparkline
                            rankHistory={ranking.rank_history}
                            maxRank={filteredRankings.length}
                            width={60}
                            height={20}
                          />
                        ) : ranking.previous_rank !== undefined ? (
                          <RankTrendIndicator
                            current={i + 1}
                            previous={ranking.previous_rank}
                          />
                        ) : (
                          <span className="text-gray-600 text-xs">--</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Tier Explanations */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { tier: 'bronze', points: '0-99', color: 'amber-600', desc: 'Getting started' },
            { tier: 'silver', points: '100-499', color: 'gray-300', desc: 'Consistent performer' },
            { tier: 'gold', points: '500-1999', color: 'yellow-400', desc: 'Proven track record' },
            { tier: 'diamond', points: '2000+', color: 'cyan-400', desc: 'Elite competitor' },
          ].map(({ tier, points, color, desc }) => (
            <div key={tier} className="bg-gray-900 border border-gray-800 rounded-md p-4 text-center">
              <ReputationBadge tier={tier} size="lg" />
              <p className="text-white font-medium mt-2 capitalize">{tier}</p>
              <p className="text-xs text-gray-500">{points} points</p>
              <p className="text-xs text-gray-400 mt-1">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
